import React, { useState } from "react";
import ProgressBar from "../progress-bar";
import { useChaseMenu } from "../../store/use-chase-menu";
import { toast } from "react-toastify";
import moment from "moment";
import CustomButton from "../customs/custom-button";
import axiosInstance from "../../utils/axiosInstance";
import { ParseErrors } from "../../utils/ParseErrors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong,
  faCopy,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";

interface IChaseDropDown {
  UpdateItemData: () => void;
}

/** Titles a region of the panel. The accent dot marks a primary section. */
const SectionLabel: React.FC<{
  children: React.ReactNode;
  dot?: boolean;
  className?: string;
}> = ({ children, dot = true, className }) => (
  <p
    className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-light-gray ${
      className ?? ""
    }`}
  >
    {dot ? (
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
    ) : null}
    {children}
  </p>
);

const ChaseDropMenu = ({ UpdateItemData }: IChaseDropDown) => {
  let portal_link = process.env.REACT_APP_PORTAL_LINK || "myportal.co.uk";
  const { initialValues } = useChaseMenu();
  const { chase } = initialValues;
  const [logs, setLogs] = useState({
    data: [...chase?.logs],
    totalCount: chase?.logs?.length,
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);

  const portalUrl = `${portal_link}/${chase?.policyReferenceNumber}`;

  // ---------------------------------------------- [ COPY TO CLIPBOARD ] --------------------------------------------------------------
  const CopytoClipboard = () => {
    navigator.clipboard.writeText(portalUrl);
    toast.success("Copied");
  };

  // ---------------------------------------------- [ HANDLE TAKE AGENT PAYMENT ] --------------------------------------------------------------
  const HandleTakePayment = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/chase/create-agent-payment-token/${chase?.policyReferenceNumber}`
      );

      window.open(`${res.data.link}`);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      toast.error(ParseErrors(error));
    }
  };

  const loadMoreLogs = async () => {
    setIsFetching(true);
    try {
      const res = await axiosInstance.get(
        `/loan/find/logs/${chase?.id}?limit=${limit}&offset=${limit + page}`
      );
      setPage(page + limit);
      setLogs((prevState) => ({
        data: [...prevState.data, ...res.data.data],
        totalCount: res.data.totalCount,
      }));
      setIsFetching(false);
    } catch (error) {
      console.log(error);
      setIsFetching(false);
    }
  };

  return (
    <td colSpan={10}>
      <div className="my-3 animate-fade-in rounded-2xl border border-edge bg-surface-muted p-4 shadow-card sm:p-5">
        {/* ---------------------------- TIMELINE + ACTIONS ---------------------------- */}
        <div className="w-full rounded-2xl border border-edge bg-surface p-5 sm:p-6">
          <div className="flex flex-col items-start gap-6 lg:flex-row">
            <div className="w-full min-w-0 lg:flex-1">
              <div className="flex w-full items-center gap-2">
                <SectionLabel>Chase Timeline</SectionLabel>
                {chase?.callId ? (
                  <FontAwesomeIcon
                    icon={faPhone}
                    title="Call logged"
                    aria-label="Call logged"
                    className="text-[11px] text-light-gray"
                  />
                ) : null}
              </div>

              <div className="pb-2 pt-6">
                <ProgressBar
                  timeline={chase?.timeline?.sort((a, b) => b.day - a.day)}
                />
              </div>
            </div>

            <div className="w-full shrink-0 lg:w-[260px]">
              <div className="h-fit w-full rounded-xl border border-edge bg-surface-elevated px-4 py-3.5">
                <SectionLabel dot={false}>Portal Link</SectionLabel>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => window.open(portalUrl, "_blank")}
                    title="Open customer portal"
                    className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-[11px] font-bold text-blue transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                  >
                    {portal_link?.replaceAll("https://", "")}/
                    {chase?.policyReferenceNumber}
                  </button>
                  <button
                    type="button"
                    onClick={CopytoClipboard}
                    title="Copy portal link"
                    aria-label="Copy portal link"
                    className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-edge bg-active-background text-blue transition-colors duration-200 hover:border-indigo-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-[13px]" />
                  </button>
                </div>
              </div>

              <CustomButton
                className="!mt-2"
                variant="primary"
                fullWidth
                onClick={HandleTakePayment}
                loading={loading}
                disabled={loading}
              >
                Take Payment
              </CustomButton>
            </div>
          </div>
        </div>

        {/* -------------------------------- LOGS -------------------------------- */}
        <div className="mt-4 w-full rounded-2xl border border-edge bg-surface p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <SectionLabel>Renewal Timeline</SectionLabel>
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-light-gray">
              Amount
            </span>
          </div>

          {logs?.data?.length ? (
            <div className="mt-2">
              {logs.data.map((item) => (
                <div
                  className="flex items-start justify-between gap-4 border-b border-edge-faint py-3.5 last:border-none sm:items-center"
                  key={item?.id}
                >
                  <h2 className="w-[86px] shrink-0 text-start text-[12px] font-semibold tabular-nums text-light-gray sm:w-[100px]">
                    {moment(item.createdAt).format("DD/MM/YYYY")}
                  </h2>

                  <h2 className="min-w-0 flex-1 break-words text-start text-[13px] font-medium capitalize text-heading-color sm:text-[14px]">
                    {item.type.replaceAll("-", " ")}{" "}
                    {item?.type === "chase-status-updated" ? (
                      <>
                        From
                        <span className="ml-[4px] font-bold">
                          {item?.data?.oldValues?.status}
                        </span>
                        <FontAwesomeIcon
                          icon={faArrowRightLong}
                          className="mx-2"
                        />
                        <span className="font-bold">
                          {item?.data?.newValues?.status}
                        </span>
                      </>
                    ) : null}
                    {item?.creator?.name ? (
                      <span className="text-light-gray normal-case">
                        {" "}
                        ({item?.creator?.name})
                      </span>
                    ) : null}
                  </h2>

                  <h2 className="w-[80px] shrink-0 text-end text-[13px] font-semibold tabular-nums text-heading-color sm:w-[96px] sm:text-[14px]">
                    £{(item?.amount ?? 0).toFixed(2)}
                  </h2>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-[13px] font-medium text-light-gray">
              No activity recorded for this chase yet.
            </p>
          )}

          {/* {logs?.length >= 10 && page <= logs.totalCount ? (
                <div className="w-[90%] mx-auto flex items-center justify-end py-2">
                  <button
                    className="text-md text-heading-color cursor-pointer"
                    onClick={loadMoreLogs}
                    disabled={isFetching}
                  >
                    <FontAwesomeIcon
                      icon={faRotateRight}
                      className="text-md text-heading-color mr-[2px]"
                      spin={isFetching}
                    />
                    Load More
                  </button>
                </div>
              ) : null} */}
        </div>
      </div>
    </td>
  );
};

export default ChaseDropMenu;
